import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Search, Filter, Layers, Package, ChevronDown, ChevronUp, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SearchAndBrowse = () => {
  const { user } = useAuth();
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedMachineModel, setSelectedMachineModel] = useState("");
  const [selectedStockStatus, setSelectedStockStatus] = useState("");
  
  // Filter options
  const [categories, setCategories] = useState([]);
  const [machineModels, setMachineModels] = useState([]);
  
  // Results state
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // View mode: 'search' for filtered results, 'browse' for category browsing
  const [viewMode, setViewMode] = useState("browse");
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [categoryItems, setCategoryItems] = useState({});

  // Active filters display
  const [activeFilters, setActiveFilters] = useState([]);

  useEffect(() => {
    fetchFilters();
  }, []);

  const fetchFilters = async () => {
    try {
      const [catRes, modelRes] = await Promise.all([
        axios.get(`${API_URL}/inventory/categories/list`),
        axios.get(`${API_URL}/inventory/machine-models/list`)
      ]);
      setCategories(catRes.data);
      setMachineModels(modelRes.data);
    } catch (error) {
      console.error("Failed to fetch filters", error);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSearch = useCallback(async (query, category, machineModel) => {
    // Only search if there's at least one filter active
    if (!query && !category && !machineModel && !selectedStockStatus) {
      setViewMode("browse");
      setResults([]);
      setActiveFilters([]);
      return;
    }

    setLoading(true);
    setViewMode("search");
    
    try {
      const params = new URLSearchParams();
      if (query) params.append('query', query);
      if (category && category !== 'all') params.append('category', category);
      if (machineModel && machineModel !== 'all') params.append('machine_model', machineModel);
      if (selectedStockStatus && selectedStockStatus !== 'all') params.append('stock_status', selectedStockStatus);
      
      const response = await axios.get(`${API_URL}/inventory/search?${params.toString()}`);
      const data = response.data;
      setResults(data.items || data);
      
      // Build active filters display
      const filters = [];
      if (query) filters.push({ type: 'query', label: `"${query}"`, value: query });
      if (category && category !== 'all') filters.push({ type: 'category', label: category, value: category });
      if (machineModel && machineModel !== 'all') filters.push({ type: 'machine_model', label: machineModel, value: machineModel });
      if (selectedStockStatus && selectedStockStatus !== 'all') filters.push({ type: 'stock_status', label: selectedStockStatus.replace('_', ' '), value: selectedStockStatus });
      setActiveFilters(filters);
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  }, [selectedStockStatus]);

  // Debounced search on input change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchQuery, selectedCategory, selectedMachineModel);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, selectedCategory, selectedMachineModel, selectedStockStatus, handleSearch]);

  const removeFilter = (filterType) => {
    if (filterType === 'query') setSearchQuery("");
    if (filterType === 'category') setSelectedCategory("");
    if (filterType === 'machine_model') setSelectedMachineModel("");
    if (filterType === 'stock_status') setSelectedStockStatus("");
  };

  const handleReset = () => {
    setSearchQuery("");
    setSelectedCategory("");
    setSelectedMachineModel("");
    setSelectedStockStatus("");
    setResults([]);
    setActiveFilters([]);
    setViewMode("browse");
    setExpandedCategory(null);
  };

  const toggleCategory = async (category) => {
    if (expandedCategory === category) {
      setExpandedCategory(null);
    } else {
      setExpandedCategory(category);
      if (!categoryItems[category]) {
        try {
          const response = await axios.get(`${API_URL}/inventory/by-category/${encodeURIComponent(category)}`);
          setCategoryItems({ ...categoryItems, [category]: response.data });
        } catch (error) {
          toast.error("Failed to fetch items");
        }
      }
    }
  };

  const quickFilterByCategory = (category) => {
    setSelectedCategory(category);
    setExpandedCategory(null);
  };

  const quickFilterByMachineModel = (model) => {
    setSelectedMachineModel(model);
  };

  if (initialLoading) {
    return (
      <div className="p-8">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  const renderItemTable = (items, showCategory = true) => (
    <table className="w-full text-sm">
      <thead className="bg-slate-100">
        <tr>
          <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Part Number</th>
          <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Name</th>
          {showCategory && <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Category</th>}
          <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Machine Model</th>
          <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Brand</th>
          <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Location</th>
          <th className="text-right px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Qty</th>
          <th className="text-right px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {items.map((item) => {
          const isLowStock = item.quantity <= item.reorder_level;
          return (
            <tr key={item.id} className="hover:bg-orange-50/50 transition-colors" data-testid={`search-result-${item.part_number}`}>
              <td className="px-4 py-3 font-medium text-slate-900">{item.part_number}</td>
              <td className="px-4 py-3 text-slate-700">{item.name}</td>
              {showCategory && (
                <td className="px-4 py-3">
                  {item.category ? (
                    <button
                      onClick={() => quickFilterByCategory(item.category)}
                      className="text-orange-600 hover:text-orange-700 hover:underline text-sm"
                    >
                      {item.category}
                    </button>
                  ) : '-'}
                </td>
              )}
              <td className="px-4 py-3">
                {item.machine_model ? (
                  <button
                    onClick={() => quickFilterByMachineModel(item.machine_model)}
                    className="text-orange-600 hover:text-orange-700 hover:underline text-sm"
                  >
                    {item.machine_model}
                  </button>
                ) : '-'}
              </td>
              <td className="px-4 py-3 text-slate-600">{item.brand || '-'}</td>
              <td className="px-4 py-3 text-slate-600">{item.location || '-'}</td>
              <td className="px-4 py-3 text-right">
                <span className={`font-semibold ${isLowStock ? 'text-orange-500' : 'text-slate-900'}`}>
                  {item.quantity}
                </span>
              </td>
              <td className="px-4 py-3 text-right">
                {item.quantity === 0 ? (
                  <span className="inline-block px-2 py-1 bg-red-100 text-red-700 text-xs font-bold uppercase rounded-sm">
                    Out of Stock
                  </span>
                ) : isLowStock ? (
                  <span className="inline-block px-2 py-1 bg-orange-100 text-orange-700 text-xs font-bold uppercase rounded-sm">
                    Low Stock
                  </span>
                ) : (
                  <span className="inline-block px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold uppercase rounded-sm">
                    In Stock
                  </span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div className="p-6 md:p-8" data-testid="search-browse-page">
      <div className="mb-8">
        <h1 className="text-4xl font-black text-slate-900 uppercase page-title" style={{ letterSpacing: '-0.02em' }}>
          Search & Browse
        </h1>
        <p className="text-slate-500 mt-1">Find parts by keyword, category, or machine model</p>
      </div>

      {/* Search Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <Label className="text-xs text-slate-600 font-bold uppercase tracking-wider">Search</Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                data-testid="search-query-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Part number, name, brand..."
                className="pl-10 rounded-sm border-slate-300 focus:ring-0 focus:border-orange-500 bg-slate-50"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-600 font-bold uppercase tracking-wider">Category</Label>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger data-testid="category-filter" className="mt-1 rounded-sm border-slate-300 focus:ring-0 focus:border-orange-500 bg-slate-50">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200 rounded-sm">
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.category} value={cat.category}>
                    {cat.category} ({cat.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600 font-bold uppercase tracking-wider">Machine Model</Label>
            <Select value={selectedMachineModel} onValueChange={setSelectedMachineModel}>
              <SelectTrigger data-testid="machine-model-filter" className="mt-1 rounded-sm border-slate-300 focus:ring-0 focus:border-orange-500 bg-slate-50">
                <SelectValue placeholder="All Models" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200 rounded-sm">
                <SelectItem value="all">All Models</SelectItem>
                {machineModels.map((model) => (
                  <SelectItem key={model.machine_model} value={model.machine_model}>
                    {model.machine_model} ({model.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600 font-bold uppercase tracking-wider">Stock Status</Label>
            <Select value={selectedStockStatus} onValueChange={setSelectedStockStatus}>
              <SelectTrigger data-testid="stock-status-filter" className="mt-1 rounded-sm border-slate-300 focus:ring-0 focus:border-orange-500 bg-slate-50">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200 rounded-sm">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="in_stock">In Stock</SelectItem>
                <SelectItem value="low_stock">Low Stock</SelectItem>
                <SelectItem value="out_of_stock">Out of Stock</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-slate-100">
            <span className="text-xs text-slate-500 font-medium">Active filters:</span>
            {activeFilters.map((filter) => (
              <span
                key={filter.type}
                className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-sm"
              >
                {filter.label}
                <button
                  onClick={() => removeFilter(filter.type)}
                  className="hover:text-orange-900"
                  data-testid={`remove-filter-${filter.type}`}
                >
                  <X size={14} />
                </button>
              </span>
            ))}
            <Button
              onClick={handleReset}
              variant="ghost"
              size="sm"
              className="text-slate-500 hover:text-slate-700 text-xs"
              data-testid="clear-all-filters"
            >
              Clear all
            </Button>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="text-slate-500">Searching...</div>
        </div>
      )}

      {/* Search Results View */}
      {!loading && viewMode === "search" && (
        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-lg font-bold text-slate-900 uppercase tracking-wider" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Search Results
            </h2>
            <p className="text-xs text-slate-500 mt-1">{results.length} items found</p>
          </div>
          {results.length === 0 ? (
            <div className="p-12 text-center">
              <Filter className="mx-auto text-slate-300 mb-4" size={64} />
              <p className="text-slate-500 text-lg">No items match your search criteria</p>
              <p className="text-xs text-slate-400 mt-2">Try adjusting your filters or search terms</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              {renderItemTable(results)}
            </div>
          )}
        </div>
      )}

      {/* Browse by Category View */}
      {!loading && viewMode === "browse" && (
        <div>
          <div className="mb-4">
            <h2 className="text-lg font-bold text-slate-900 uppercase tracking-wider" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Browse by Category
            </h2>
            <p className="text-xs text-slate-500 mt-1">Click a category to expand and view items</p>
          </div>

          {categories.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-sm p-12 text-center">
              <Layers className="mx-auto text-slate-300 mb-4" size={64} />
              <p className="text-slate-500 text-lg">No categories available</p>
              <p className="text-xs text-slate-400 mt-1">Add category information to items to see them organized here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {categories.map((cat) => (
                <div key={cat.category} className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleCategory(cat.category)}
                    onKeyDown={(e) => e.key === 'Enter' && toggleCategory(cat.category)}
                    data-testid={`category-${cat.category}`}
                    className="w-full p-5 flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-orange-100 rounded-sm flex items-center justify-center">
                        <Layers className="text-orange-600" size={20} strokeWidth={2} />
                      </div>
                      <div className="text-left">
                        <h3 className="text-base font-bold text-slate-900 uppercase" style={{ fontFamily: 'Chivo, sans-serif' }}>
                          {cat.category}
                        </h3>
                        <p className="text-sm text-slate-500">{cat.count} items</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          quickFilterByCategory(cat.category);
                        }}
                        className="text-xs"
                      >
                        Filter by this
                      </Button>
                      {expandedCategory === cat.category ? (
                        <ChevronUp className="text-slate-400" size={24} />
                      ) : (
                        <ChevronDown className="text-slate-400" size={24} />
                      )}
                    </div>
                  </div>
                  
                  {expandedCategory === cat.category && categoryItems[cat.category] && (
                    <div className="border-t border-slate-200 overflow-x-auto">
                      {renderItemTable(categoryItems[cat.category], false)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Machine Models Quick Access */}
          {machineModels.length > 0 && (
            <div className="mt-8">
              <h2 className="text-lg font-bold text-slate-900 uppercase tracking-wider mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Quick Filter by Machine Model
              </h2>
              <div className="flex flex-wrap gap-2">
                {machineModels.map((model) => (
                  <Button
                    key={model.machine_model}
                    variant="outline"
                    size="sm"
                    onClick={() => quickFilterByMachineModel(model.machine_model)}
                    className="text-sm border-slate-300 hover:border-orange-500 hover:text-orange-600"
                    data-testid={`machine-model-${model.machine_model}`}
                  >
                    {model.machine_model} ({model.count})
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchAndBrowse;
